import { useApolloClient } from '@apollo/client'
import { useLocale, Collection, ListBoxLoadMoreItem, Text } from 'react-aria-components'
import { ALL_FACILITIES_QUERY } from '~/client/queries'
import { useFeedbackDialogContext } from '~/components/ErrorBoundary/context'
import { SelectItem, Select } from '~/ui/Select'
import { Spinner } from '~/ui/Spinner'
import { useAsyncList } from 'react-stately'
import { AllFacilitiesQuery } from '~/gql/graphql'
import { captureException } from '@sentry/react'

type FacilityItem = NonNullable<NonNullable<NonNullable<AllFacilitiesQuery['allFacilities']>['facilities']>[number]>

export const FacilityDropDown = () => {
  const { locale } = useLocale()
  const client = useApolloClient()
  const list = useAsyncList<FacilityItem, number>({
    async load({ cursor }) {
      const { data } = await client
        .query({
          query: ALL_FACILITIES_QUERY,
          variables: { orderBy: 'updatedAt_DESC', limit: 10, page: cursor ?? 0, allUsers: false },
        })
        .catch(e => {
          captureException(e)
          return { data: null }
        })
      const result = (data?.allFacilities?.facilities ?? []).filter(item => item !== null)
      return {
        items: result,
        cursor: result.length > 0 ? (cursor ?? 0) + 1 : undefined,
      }
    },
    getKey: item => item.id,
  })

  const { facilityId, setFormData } = useFeedbackDialogContext()

  return (
    <Select
      isRequired
      label="Select the relevant facility"
      selectedKey={facilityId}
      onSelectionChange={key => setFormData({ facilityId: key as number | null })}
    >
      <Collection items={list.items}>
        {item => (
          <SelectItem id={item.id} textValue={item.name}>
            <div className="flex flex-col">
              <Text slot="label">{item.name}</Text>
              <Text slot="description" className="text-sm opacity-50">
                Created {new Date(item.updatedAt).toLocaleString(locale)}
              </Text>
            </div>
          </SelectItem>
        )}
      </Collection>
      <ListBoxLoadMoreItem
        className="flex justify-center p-3"
        onLoadMore={list.loadMore}
        isLoading={list.loadingState === 'loadingMore'}
      >
        <Spinner />
      </ListBoxLoadMoreItem>
    </Select>
  )
}
